import { FileWarning, SearchX } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';
import { Link, useParams } from 'react-router';

import { BackLink } from '@/components/layout/BackLink';
import { GatedChrome } from '@/components/auth/GatedChrome';
import type { Crumb } from '@/components/layout/page-chrome-context';
import { NoAccessPanel } from '@/components/ui/NoAccessPanel';
import { ApiError } from '@/lib/api';
import { useActiveCompany } from '@/lib/active-company-context';
import { type BackTarget, useBackTarget } from '@/lib/back-nav';
import { BackToListLink } from '@/features/master-list/components/BackToListLink';
import { useMasterListAccess } from '@/features/master-list/use-master-list-access';

import { EditorBody } from './components/EditorBody';
import { EditorSkeleton } from './components/EditorSkeleton';
import { useFlowCapabilities } from './use-flow-capabilities';
import { StatePanel } from '@/components/ui/StatePanel';
import { errorAdvice } from '@/lib/error-advice';

function breadcrumbFor(code: string, entryId: string, back: BackTarget): Crumb[] {
  const dossier = `/documentos/${entryId}`;
  // Si se vino de la ficha, su miga la abre con el mismo regreso que el botón.
  const crumb: Crumb =
    back.to === dossier && back.state
      ? { label: code, to: dossier, state: back.state }
      : { label: code, to: dossier };
  return [
    { label: 'QMS', to: '/dashboard', strong: true },
    { label: 'Lista Maestra', to: '/documentos' },
    crumb,
    { label: 'Edición', strong: true },
  ];
}

/** Envuelve el chrome (breadcrumb + regreso al origen) alrededor de cualquier estado. */
function Frame({
  code,
  entryId,
  children,
}: {
  code: string | null;
  entryId: string;
  children: ReactNode;
}): ReactElement {
  // Sin origen (deep-link) el padre es la ficha del documento, no la lista general.
  const back = useBackTarget({
    to: `/documentos/${entryId}`,
    label: code ? `Ficha ${code}` : 'Ficha del documento',
  });
  // G4 (FUN-06): sin acceso a documentos las migas no nombran la Lista Maestra.
  const access = useMasterListAccess();
  return (
    <>
      <GatedChrome
        state={access.state}
        breadcrumb={breadcrumbFor(code ?? 'Documento', entryId, back)}
        navContext="Editor y flujo"
      />
      <BackLink target={back} />
      {children}
    </>
  );
}

export function DocumentEditor(): ReactElement {
  const { id = '' } = useParams<{ id: string }>();
  const { activeCompanyId } = useActiveCompany();
  const query = useFlowCapabilities(id, {
    companyId: activeCompanyId,
    enabled: Boolean(activeCompanyId) && id !== '',
  });

  if (!activeCompanyId || query.isLoading) {
    return (
      <Frame code={null} entryId={id}>
        <EditorSkeleton />
      </Frame>
    );
  }

  if (query.isError) {
    // 404/400 = documento inexistente/ajeno o id malformado (deep-link viejo): "no
    // encontrado" honesto, no un error de servidor con "Reintentar" que no funcionaría.
    const status = query.error instanceof ApiError ? query.error.status : null;
    const notFound = status === 404 || status === 400;
    return (
      <Frame code={null} entryId={id}>
        {status === 403 ? (
          // Sin permiso (p. ej. proveedor con acceso de solo lectura): reintentar no cambia
          // nada; el documento se consulta desde su ficha.
          <NoAccessPanel title="No tienes acceso al editor de este documento">
            Tu rol no permite operar su flujo.{' '}
            <Link to={`/documentos/${id}`}>Volver a la ficha del documento</Link>.
          </NoAccessPanel>
        ) : notFound ? (
          <StatePanel icon={<SearchX />} title="No encontramos este documento" role="alert">
            Puede haber sido dado de baja o no pertenece a la empresa activa. <BackToListLink />.
          </StatePanel>
        ) : (
          <StatePanel
            icon={<FileWarning />}
            title="No se pudo cargar el editor"
            tone="danger"
            role="alert"
            onRetry={() => void query.refetch()}
          >
            {errorAdvice(query.error)}
          </StatePanel>
        )}
      </Frame>
    );
  }

  if (!query.data) {
    return (
      <Frame code={null} entryId={id}>
        <EditorSkeleton />
      </Frame>
    );
  }

  return (
    <Frame code={query.data.code} entryId={id}>
      <EditorBody caps={query.data} companyId={activeCompanyId} />
    </Frame>
  );
}
